import { errorSchema } from "@app/shared";
import type { ZodType } from "zod";
import { ApiError } from "../lib/api";

/**
 * Les ecritures de la console, calquees sur `apiGet` du template : meme `ApiError`, meme
 * lecture de `errorSchema`. `lib/api.ts` appartient au template et n'a ni POST ni PUT.
 *
 * Elles vivent ici, a part, parce que trois modules s'en servent (le coloriage, le bouton
 * « C'est fait » du portefeuille, le minimum et la reserve des bobines) et qu'aucun module
 * n'importe les fichiers d'un autre.
 */

/**
 * Une ecriture JSON lue par un schema Zod. Les refus du backend deviennent une `ApiError` avec
 * son code et son message, que la page affiche ; une requete qui ne part pas (reseau coupe)
 * aussi, avec le statut 0.
 */
async function apiEcrire<T>(
  methode: "POST" | "PUT",
  path: string,
  corps: unknown,
  schema: ZodType<T>,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: methode,
      headers: { accept: "application/json", "content-type": "application/json" },
      body: JSON.stringify(corps),
    });
  } catch {
    throw new ApiError("reseau", "La console ne répond pas : rien n'a été enregistré.", 0);
  }
  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const parsed = errorSchema.safeParse(payload);
    throw new ApiError(
      parsed.success ? parsed.data.error.code : "http_error",
      parsed.success ? parsed.data.error.message : `Erreur HTTP ${response.status}`,
      response.status,
    );
  }

  const result = schema.safeParse(payload);
  if (!result.success) {
    throw new ApiError("contract_broken", "Réponse hors contrat", response.status);
  }
  return result.data;
}

/** Un POST JSON : la forme de presque toutes les ecritures de la console. */
export function apiPost<T>(path: string, corps: unknown, schema: ZodType<T>): Promise<T> {
  return apiEcrire("POST", path, corps, schema);
}

/**
 * Un PUT JSON : modifier une variante du coloriage (ticket 4), regler le minimum et la reserve
 * des bobines (#220).
 */
export function apiPut<T>(path: string, corps: unknown, schema: ZodType<T>): Promise<T> {
  return apiEcrire("PUT", path, corps, schema);
}
